// Razorpay service for frontend
import Api from'./api';
import { fetchConfig } from'./configService';

// Helper to get token from localStorage (similar to vehiQuick's TokenService approach)
const getToken = () => {
 return localStorage.getItem('uttaapp_token') || localStorage.getItem('token');
};

// Initiate Razorpay payment (matching vehiQuick implementation)
export const initiateRazorpayPayment = async (
 amount,
 bookingId,
 user,
 onSuccess,
 onFailure
) => {
 try {
 // 1. Create Razorpay order in backend
 const token = getToken();
 const orderResponse = await fetch(Api.Payment.createRazorpayOrder, {
 method:"POST",
 headers: {"Content-Type":"application/json",
 ...(token ? { Authorization:`Bearer ${token}`} : {}),
 },
 body: JSON.stringify({
 amount: amount,
 currency:"INR",
 bookingId: bookingId,
 }),
 });

 const orderData = await orderResponse.json();
 
 if (!orderData.success) {
 throw new Error(orderData.message ||"Failed to create Razorpay order");
 }

 const { razorpayOrder } = orderData;

 // 2. Get Razorpay key from backend config
 const config = await fetchConfig();
 const razorpayKeyId = config.razorpayKeyId ||"rzp_test_R7tBMOOLxOu2dr";

 // 3. Prepare payment options
 const options = {
 key: razorpayKeyId,
 amount: razorpayOrder.amount,
 currency: razorpayOrder.currency,
 name:"UttaApp",
 description:`Payment for Booking ${bookingId}`,
 order_id: razorpayOrder.id,
 prefill: {
 email: user?.email ||"user@example.com",
 contact: user?.phone ||"9999999999",
 name: user?.name ||"User",
 },
 theme: { color:"#1976D2"},
 };

 // 4. Web logic: load Razorpay's Checkout JS and open payment
 const scriptId ="razorpay-checkout-js";
 if (!document.getElementById(scriptId)) {
 const script = document.createElement("script");
 script.id = scriptId;
 script.src ="https://checkout.razorpay.com/v1/checkout.js";
 document.body.appendChild(script);
 await new Promise((resolve) => {
 script.onload = resolve;
 });
 }

 return new Promise((resolve, reject) => {
 const rzp = new window.Razorpay({
 ...options,
 handler: async function (response) {
 try {
 // Payment successful - update backend with signature data
 await updatePaymentStatus(
 bookingId,
 response.razorpay_payment_id,'completed','razorpay',
 response // Pass full response for signature verification
 );
 
 onSuccess && onSuccess(response);
 resolve(response);
 } catch (error) {
 onFailure && onFailure(error);
 reject(error);
 }
 },
 modal: {
 ondismiss: () => {
 const error = new Error("Payment popup closed");
 onFailure && onFailure(error);
 reject(error);
 },
 },
 });
 rzp.open();
 });
 } catch (error) {
 console.error("Error initiating Razorpay payment:", error);
 onFailure && onFailure(error);
 throw error;
 }
};

// Update payment status (matching vehiQuick implementation)
export const updatePaymentStatus = async (bookingId, paymentId, status, paymentMethod, razorpayData = null) => {
 try {
 const token = getToken();
 const requestBody = {
 bookingId,
 paymentStatus: status,
 transactionId: paymentId,
 paymentMethod: paymentMethod,
 };

 // If Razorpay payment, include signature data for verification
 if (paymentMethod ==='razorpay'&& razorpayData) {
 requestBody.razorpayPaymentId = razorpayData.razorpay_payment_id || paymentId;
 requestBody.razorpayOrderId = razorpayData.razorpay_order_id;
 requestBody.razorpaySignature = razorpayData.razorpay_signature;
 }

 const response = await fetch(Api.Payment.updatePaymentStatus, {
 method:"POST",
 headers: {"Content-Type":"application/json",
 ...(token ? { Authorization:`Bearer ${token}`} : {}),
 },
 body: JSON.stringify(requestBody),
 });
 
 const data = await response.json();
 
 if (!data.success) {
 throw new Error(data.message ||"Failed to update payment status");
 }
 
 return data;
 } catch (error) {
 console.error("Error updating payment status:", error);
 throw error;
 }
};

// Simple cash payment processing (matching vehiQuick)
export const processCashPayment = async (bookingId) => {
 try {
 const response = await updatePaymentStatus(bookingId,`cash_${Date.now()}`,'completed','cash');
 return response;
 } catch (error) {
 console.error("Error processing cash payment:", error);
 throw error;
 }
};

// Simple UPI payment processing (for testing) (matching vehiQuick)
export const processUPIPayment = async (bookingId, _upiId) => {
 try {
 const response = await updatePaymentStatus(bookingId,`upi_${Date.now()}`,'completed','upi');
 return response;
 } catch (error) {
 console.error("Error processing UPI payment:", error);
 throw error;
 }
};

// Create Razorpay order (for backward compatibility and direct usage)
export const createRazorpayOrder = async (bookingId, amount, token) => {
 try {
 // Ensure API endpoint is defined
 const apiUrl = Api.Payment?.createRazorpayOrder;
 if (!apiUrl) {
 console.error('❌ API Payment endpoint is undefined:', Api);
 throw new Error('Payment API endpoint is not configured. Please contact support.');
 }

 console.log('🔗 Creating Razorpay order at:', apiUrl);
 console.log('📦 Request data:', { bookingId, amount });
 
 const authToken = token || getToken();
 if (!authToken) {
 throw new Error('Authentication token is missing. Please login again.');
 }

 const response = await fetch(apiUrl, {
 method:'POST',
 headers: {'Content-Type':'application/json','Authorization':`Bearer ${authToken}`},
 body: JSON.stringify({
 bookingId,
 amount: parseFloat(amount) // Ensure amount is a number
 })
 });

 console.log('📡 Response status:', response.status, response.statusText);

 // Check if response is ok
 if (!response.ok) {
 let errorData;
 try {
 errorData = await response.json();
 } catch (parseError) {
 // If response is not JSON, create error from status
 if (response.status === 401 || response.status === 403) {
 throw new Error('Authentication failed. Please login again.');
 }
 throw new Error(`HTTP error! status: ${response.status}`);
 }
 
 // Handle authentication errors specifically
 if (response.status === 401 || response.status === 403) {
 throw new Error(errorData.message ||'Authentication failed. Please login again.');
 }
 
 // For 500 errors, use the backend message or a generic error
 if (response.status === 500) {
 // Check if it's a Razorpay authentication error
 if (errorData.message && errorData.message.includes('Razorpay authentication')) {
 throw new Error('Payment gateway configuration error. Please contact support.');
 }
 throw new Error(errorData.message ||'Server error. Please try again later.');
 }
 
 // For 400 errors (including Razorpay auth errors from backend)
 if (response.status === 400 && errorData.message?.includes('Razorpay')) {
 throw new Error(errorData.message ||'Payment gateway error. Please try cash payment or contact support.');
 }
 
 throw new Error(errorData.message ||`HTTP error! status: ${response.status}`);
 }

 const data = await response.json();
 if (!data.success) {
 throw new Error(data.message ||'Failed to create payment order');
 }
 return data;
 } catch (error) {
 console.error('Error creating Razorpay order:', error);
 // Provide more specific error message
 if (error.message) {
 throw error;
 }
 throw new Error('Failed to create payment order. Please check your connection and try again.');
 }
};

